import React, { createContext, useContext } from 'react';

import { noop } from 'ui-components/default-props/noop';
import { useCreateReducer } from 'ui-components/hooks/use-create-reducer';

import { WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { useGetInitImport } from '../../data/hooks/use-get-init-import';
import { useGetMappingList } from '../../data/hooks/use-get-mapping-list';
import { useStartImport } from '../../data/hooks/use-start-import';
import { useCreateInitRequest } from '../hooks/use-create-init-request';
import { useCreateMapping } from '../hooks/use-create-mapping';
import { useCreateMappingName } from '../hooks/use-create-mapping-name';
import { useGetPreviewData } from '../hooks/use-get-preview-data';

/**
 * @typedef {import("../../data/types/types").ImportMapping} ImportMapping
 * @typedef {import("../../data/types/types").MappedColumn} MappedColumn
 * @typedef {import("../../data/types/types").TemplateAttribute} TemplateAttribute
 */

/**
 * @typedef {object} ImportContextValue
 * @property {object} form
 * @property {Function} dispatch
 * @property {ImportTypes} importType
 * @property {(step: string) => void} onClose
 * @property {(onSuccess: () => void) => void} onImport
 * @property {(workflowStep: string, metadata?: object) => void} trackProcess
 * @property {boolean} isImportLoading
 * @property {() => void} onComplete
 * @property {(shipstationField: string) => React.ReactNode} translateField
 * @property {string} mappingName
 * @property {(name: string) => void} setMappingName
 * @property {Array<ImportMapping>} mappingList
 * @property {(uniqueId: string) => ImportMapping | undefined} getMappingById
 * @property {(mappingName: string) => boolean} isMappingNameUnique
 * @property {(mappingId: string) => void} deleteMappingTemplate
 * @property {(payload: import("../../data/types/types").UpdateMappingTemplatePayload) => void} updateMappingTemplate
 * @property {boolean} isGetMappingsLoading
 * @property {import("../../data/types/types").ImportInitRequest} initRequest
 * @property {(mapping: ImportMapping) => void} setMapping
 * @property {string|null} jobId
 * @property {import("../../data/types/types").InitImportDefinition} definition
 * @property {string|null} initError
 * @property {boolean} isInitImportLoading
 * @property {(onSuccessfulExecution: () => void) => void} initImport
 * @property {() => void} resetRowNumber
 * @property {() => void} getNextDataRow
 * @property {() => void} getPreviousDataRow
 * @property {() => number} getRowNumbers
 * @property {() => number} getCurrentRowNumber
 * @property {(csvColumn: string) => string} getCsvColumnFromDefinition
 * @property {MappedColumn[]} mappedColumns
 * @property {Array<import("../../data/types/types").CsvColumn>} remainingColumns
 * @property {boolean} isMappingResultValid
 * @property {(templateAttribute: TemplateAttribute, csvColumn: string) => void} updateMapping
 * @property {(templateAttribute: TemplateAttribute) => MappedColumn | undefined} getMappedCsvColumn
 * @property {(attributeCode: string) => Array<import("../../data/types/types").MappingError>} getMappingErrors
 * @property {() => boolean} validateUserMapping
 * @property {(file: File) => void} setFile
 */

/**
 * Context for managing CSV import functionality.
 * @type {React.Context<ImportContextValue|null>}
 */
const ImportContext = createContext();

/**
 * React hook that exposes the current ImportContext value.
 * Throws Error when used outside of ImportContext Provider.
 * @throws {Error} If used outside of and ImportContext Provider
 * @returns {ImportContextValue} The ImportContext value
 */
export const useImportContext = () => {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error('ImportContext must be used within ImportProvider');
  }
  return context;
};

/** @typedef {import('../../data/types/import-type').ImportTypes} ImportTypes */

/**
 * @param {object} props
 * @param {(step: string) => void} props.onClose
 * @param {React.ReactNode} props.children
 * @param {ImportTypes} props.importType
 * @param {(workflowStep: string, metadata: Record<string, string>) => void} props.trackProcess
 * create config step may vary depending on the type of import.
 * if you want the data to be persistent while going through the steps
 * and be able to update this data from the step level, use these props.
 * @param {object} [props.actionMap]
 * @param {(initState: object) => object} [props.init]
 * @param {object} [props.initialState]
 * @param {(shipstationField: string) => React.ReactNode} props.translateField
 * then by initiating the import (initImport) you decide what to send to the server,
 * because it takes a dictionary as the settings value.
 * @param {(form: object) => object} [props.createCustomImportSettings]
 * @param {string} props.pusherEvent
 * @param {string} [props.workflowType]
 * @param {() => void} [props.onComplete]
 * @param {string[]} [props.attributesToHide]
 * @param {string[]} [props.attributesToRequire]
 * @returns {JSX.Element}
 */

export const ImportProvider = ({
  onClose,
  children,
  importType,
  trackProcess,
  actionMap = {},
  init,
  initialState = {},
  translateField,
  createCustomImportSettings = () => null,
  pusherEvent,
  workflowType,
  onComplete = noop,
  attributesToHide = [],
  attributesToRequire = [],
}) => {
  const [form, dispatch] = useCreateReducer(actionMap, initialState, init);

  const mappingListHandler = useGetMappingList(importType);

  const initRequestHandler = useCreateInitRequest(importType);

  const mappingNameHandler = useCreateMappingName(initRequestHandler.initRequest.mapping.name);

  const { initImport, ...initResponseHandler } = useGetInitImport(initRequestHandler.initRequest, attributesToHide, attributesToRequire);

  const previewDataHandler = useGetPreviewData(initResponseHandler.definition.preview);

  const createMappingHandler = useCreateMapping(initResponseHandler.definition);

  const { startImport, isImportLoading } = useStartImport(pusherEvent, workflowType);

  const onInitImport = (onSuccessfulExecution) => {
    initImport(
      onSuccessfulExecution,
      (request) => {
        trackProcess(WorkflowSteps.CSV_IMPORT_INIT_IMPORT, {
          request: {
            file: {
              name: request.csvFile.name,
              size: request.csvFile.size,
            },
            importType: request.importType,
            mapping: request.mapping,
          },
        });
        previewDataHandler.resetRowNumber();
        onSuccessfulExecution();
      },
      (errors) => trackProcess(WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR, { errors }),
    );
  };

  const onImport = (onSuccess) => {
    const getValidatedMappings = (mappedColumns) => {
      return mappedColumns.reduce((acc, mappedColumn) => {
        if (mappedColumn.csvColumn) {
          acc.push({
            code: mappedColumn.code,
            csvColumn: mappedColumn.csvColumn,
          });
        }
        return acc;
      }, []);
    };

    const request = {
      settings: createCustomImportSettings(form),
      mappings: getValidatedMappings(createMappingHandler.mappedColumns),
      jobId: initResponseHandler.jobId,
      importType,
      importMappingId: initRequestHandler.initRequest.mapping.mappingId,
      importMappingName: mappingNameHandler.mappingName,
    };

    startImport(
      request,
      () => trackProcess(WorkflowSteps.CSV_IMPORT_START_IMPORT, { request }),
      onSuccess,
      (error) => trackProcess(WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR, { error }),
    );
  };

  /**
   * ImportContext value object that provides values and methods to consumers of the ImportContext.
   * @type {ImportContextValue}
   */
  const value = {
    form,
    dispatch,
    importType,

    onClose,
    onImport,
    trackProcess,
    isImportLoading,
    onComplete,

    translateField,

    ...mappingNameHandler,
    ...mappingListHandler,
    ...initRequestHandler,
    ...initResponseHandler,
    initImport: onInitImport,
    ...previewDataHandler,
    ...createMappingHandler,
  };

  return <ImportContext.Provider value={value}>{children}</ImportContext.Provider>;
};
